import type {HMBlockImage} from '@seed-hypermedia/client/hm-types'
import {
  HMAccountsMetadata,
  HMDocumentInfo,
  HMQueryBlockItemSummary,
  HMResourceFetchResult,
  UnpackedHypermediaId,
} from '@seed-hypermedia/client/hm-types'
import {
  findFirstBlock,
  hmId,
  plainTextOfContent,
  useRouteLink,
  useUniversalAppContext,
  type DocumentRoute,
} from '@shm/shared'
import {useDocumentActions} from '@shm/shared/document-actions-context'
import {useInteractionSummary} from '@shm/shared/models/interaction-summary'
import {
  canShowMoveDocumentAction,
  canShowRepublishDocumentAction,
  type DocumentCardActionOrigin,
} from '@shm/shared/utils/document-actions'
import {createWebHMUrl, getVersionHeads, hmIdToURL} from '@shm/shared/utils/entity-id-url'
import {useNavigate} from '@shm/shared/utils/navigation'
import {Copy, FilePen, Forward, History, Layers, MessageSquare, Pencil, Split} from 'lucide-react'
import {HTMLAttributes, ReactNode, useMemo} from 'react'
import {Button} from './button'
import {createCopyLinkMenuItem, getWebCopyLinkHostname} from './copy-link-menu'
import {copyUrlToClipboardWithFeedback} from './copy-to-clipboard'
import {DocumentKindIcon, getDocumentKind, type DocumentKind} from './document-kind-icon'
import {createDocumentVersionsPanelRoute} from './document-versions-panel'
import {DraftBadge} from './draft-badge'
import {FacePile} from './face-pile'
import {useImageUrl} from './get-file-url'
import {useHighlighter} from './highlight-context'
import {Download, Trash} from './icons'
import {MergedBadge} from './merged-badge'
import {MenuItemType, OptionsDropdown} from './options-dropdown'
import {PrivateBadge} from './private-badge'
import {SizableText} from './text'
import {Tooltip} from './tooltip'
import {cn} from './utils'

/** Builds the DocumentCard inline menu items */
export function useDocumentCardMenuItems(
  docId: UnpackedHypermediaId,
  doc?: HMResourceFetchResult['document'] | null,
  relocationOrigin?: DocumentCardActionOrigin,
): MenuItemType[] {
  const actions = useDocumentActions()
  const draft = actions.getDraft?.(docId)
  const navigate = useNavigate()
  const {onCopyReference, onPushReference, origin, experiments} = useUniversalAppContext()
  const draftId = actions.getDraftId?.(docId) ?? draft?.id
  const isOwner = actions.selectedAccountUid === docId.uid
  const hasPath = !!docId.path?.length
  const selectedAccountCanWriteSource = isOwner || !!actions.canWriteDocument?.(docId)

  return useMemo(() => {
    const items: MenuItemType[] = []
    if (actions.onEditDocument && isOwner) {
      items.push({
        key: 'edit',
        label: draftId ? 'Resume Editing' : 'Edit',
        icon: <Pencil className="size-4" />,
        onClick: (e) => {
          e?.stopPropagation()
          actions.onEditDocument!(docId, draftId)
        },
      })
    }
    items.push({
      key: 'versions',
      label: 'Versions history',
      icon: <History className="size-4" />,
      onClick: (e) => {
        e?.stopPropagation()
        navigate({
          key: 'document',
          id: docId,
          panel: createDocumentVersionsPanelRoute(docId),
        } as any)
      },
    })
    if (isOwner) {
      items.push({
        key: 'options',
        label: 'Document Settings',
        icon: <FilePen className="size-4" />,
        onClick: (e) => {
          e?.stopPropagation()
          navigate({key: 'document', id: docId, panel: {key: 'options'}} as any)
        },
      })
    }
    if (actions.onCopyLink) {
      const copyCanonical = onCopyReference ? () => onCopyReference(docId) : null
      const copyGateway = async () => {
        const gwUrl = getWebCopyLinkHostname(origin)
        const url = createWebHMUrl(docId.uid, {
          path: docId.path,
          version: docId.version,
          latest: docId.latest,
          blockRef: docId.blockRef,
          blockRange: docId.blockRange,
          hostname: gwUrl,
        })
        await copyUrlToClipboardWithFeedback(url, 'Gateway')
        onPushReference?.(docId)
      }
      items.push(
        createCopyLinkMenuItem({
          advanced: experiments?.advancedCopyLinkOptions,
          label: 'Copy link',
          canonical: {copy: copyCanonical},
          gateway: {copy: copyGateway},
          hypermedia: {
            copy: () => copyUrlToClipboardWithFeedback(hmIdToURL(docId), 'Hypermedia'),
          },
        }),
      )
    }
    if (
      actions.onMoveDocument &&
      canShowMoveDocumentAction({
        id: docId,
        selectedAccountUid: actions.selectedAccountUid,
        canWriteSource: selectedAccountCanWriteSource,
      })
    ) {
      items.push({
        key: 'move',
        label: 'Move',
        icon: <Forward className="size-4" />,
        onClick: (e) => {
          e?.stopPropagation()
          actions.onMoveDocument!(docId, relocationOrigin)
        },
      })
    }
    if (actions.onDuplicateDocument && isOwner && !!docId.path?.length) {
      items.push({
        key: 'duplicate',
        label: 'Duplicate document',
        icon: <Copy className="size-4" />,
        onClick: (e) => {
          e?.stopPropagation()
          actions.onDuplicateDocument!(docId)
        },
      })
    }
    if (
      actions.onRepublishDocument &&
      canShowRepublishDocumentAction({id: docId, selectedAccountUid: actions.selectedAccountUid})
    ) {
      items.push({
        key: 'republish',
        label: 'Republish',
        icon: <Split className="size-4" />,
        onClick: (e) => {
          e?.stopPropagation()
          actions.onRepublishDocument!(docId, relocationOrigin)
        },
      })
    }
    if (actions.onExportDocument && doc) {
      items.push({
        key: 'export',
        label: 'Export document',
        icon: <Download className="size-4" />,
        onClick: (e) => {
          e?.stopPropagation()
          actions.onExportDocument!(doc)
        },
      })
    }
    items.push({
      key: 'directory',
      label: 'Sub documents',
      icon: <Layers className="size-4" />,
      onClick: (e) => {
        e?.stopPropagation()
        navigate({key: 'directory', id: docId} as any)
      },
    })
    if (actions.onDeleteDocument && isOwner && hasPath) {
      items.push({
        key: 'delete',
        label: 'Delete document',
        icon: <Trash className="size-4" />,
        variant: 'destructive' as const,
        onClick: (e) => {
          e?.stopPropagation()
          actions.onDeleteDocument!(docId)
        },
      })
    }
    return items
  }, [
    actions,
    docId,
    selectedAccountCanWriteSource,
    hasPath,
    doc,
    isOwner,
    navigate,
    onCopyReference,
    onPushReference,
    origin,
    experiments?.advancedCopyLinkOptions,
    draftId,
    relocationOrigin,
  ])
}

/**
 * Shared container className for card-style document representations
 * (published {@link DocumentCard} and unpublished draft placeholders alike).
 */
export function documentCardContainerClassName({
  banner = false,
  hasCover = false,
}: {banner?: boolean; hasCover?: boolean} = {}) {
  return cn(
    'group/item hover:bg-accent dark:hover:bg-accent @container flex w-full overflow-hidden rounded-lg border border-border bg-white shadow-md transition-colors duration-300 dark:bg-black',
    banner && hasCover && 'rounded-xl md:min-h-[240px] lg:min-h-[280px]',
    banner && !hasCover && 'rounded-xl',
  )
}

/** Left-hand thumbnail for a document card: wide cover, square icon, or the document type placeholder. */
export function DocumentCardThumbnail({
  coverImage,
  iconImage,
  kind = 'document',
  banner = false,
}: {
  coverImage?: string
  iconImage?: string
  /** Picks the placeholder icon when no image is available. */
  kind?: DocumentKind
  banner?: boolean
}) {
  const imageUrl = useImageUrl()
  if (coverImage) {
    // Cover image. Banner cards keep a half width cover.
    // Regular row cards get a rectangle that stretches
    // vertically to fill the card's row height.
    return (
      <div
        className={cn(
          'relative m-3 h-24 shrink-0 overflow-hidden rounded-md @md:m-3 @md:h-auto',
          banner ? '@md:w-1/2' : '@md:w-32',
        )}
      >
        <img className="absolute top-0 left-0 h-full w-full object-cover" src={imageUrl(coverImage, 'L')} alt="" />
      </div>
    )
  }
  if (iconImage) {
    // No cover, but the doc has an icon — render it as a square
    // thumbnail aligned top-left next to the title.
    return (
      <div className="bg-muted m-3 flex aspect-square size-12 shrink-0 items-center justify-center overflow-hidden rounded-md @md:size-14">
        <img src={imageUrl(iconImage, 'S')} alt="" className="size-full object-cover" />
      </div>
    )
  }
  // Neither cover nor icon — use the document type placeholder.
  return (
    <div className="m-3 flex aspect-square size-12 shrink-0 items-center justify-center rounded-md bg-emerald-100 @md:size-14 dark:bg-emerald-900/30">
      <DocumentKindIcon kind={kind} className="size-6 text-emerald-700 dark:text-emerald-400" strokeWidth={1.5} />
    </div>
  )
}

/**
 * Presentational body of a document card: thumbnail, title, summary, badges, and an
 * actions row. Kept separate from {@link DocumentCard} so unpublished draft placeholders
 * can render with pixel-identical structure — only their slot contents differ.
 */
export function DocumentCardShell({
  interactive = false,
  hasCover = false,
  thumbnail,
  title,
  summary,
  details,
  badges,
  actionDetails,
  actions,
}: {
  interactive?: boolean
  hasCover?: boolean
  thumbnail: ReactNode
  title: ReactNode
  summary?: ReactNode
  details?: ReactNode
  badges?: ReactNode
  actionDetails?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div
      className={cn(
        'flex max-w-full min-w-0 flex-1 flex-col @md:flex-row',
        interactive && 'cursor-pointer',
        // Stack items vertically in narrow containers like grid items.
        // Switch to horizontal once the card has room.
        '@md:items-center',
        hasCover && '@md:items-stretch',
      )}
    >
      {thumbnail}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col justify-between">
        <div className="p-3">
          {title}
          {badges}
          {summary}
          {details}
        </div>
        <div data-testid="document-card-action-row" className="flex items-center justify-between py-2 pr-2 pl-3">
          <div className="flex items-center gap-3">{actionDetails}</div>
          {actions}
        </div>
      </div>
    </div>
  )
}

export function DocumentCard({
  docId,
  route,
  entity,
  metadata,
  firstImageInContent,
  isCollection,
  visibility,
  version,
  interactionSummary: interactionSummaryProp,
  accountsMetadata,
  contributorUids,
  navigate: navigateProp = true,
  titleLinkOnly = false,
  onMouseEnter,
  onMouseLeave,
  banner = false,
  showSummary = false,
  details,
  actionDetails,
  hideInlineActions = false,
  showCommentAction = true,
  relocationOrigin,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  docId: UnpackedHypermediaId
  /** Optional document route for callers that need to preserve an active panel. */
  route?: DocumentRoute
  entity: HMResourceFetchResult | null | undefined
  metadata?: HMDocumentInfo['metadata']
  /** Indexer-derived fallback cover (see HMDocumentInfo.firstImageInContent). */
  firstImageInContent?: HMDocumentInfo['firstImageInContent']
  /** Collection status from the document listing, used for the fallback icon. */
  isCollection?: HMDocumentInfo['isCollection']
  visibility?: HMDocumentInfo['visibility']
  version?: string
  interactionSummary?: HMQueryBlockItemSummary | null
  accountsMetadata?: HMAccountsMetadata
  contributorUids?: string[]
  navigate?: boolean
  titleLinkOnly?: boolean
  onMouseEnter?: (id: UnpackedHypermediaId) => void
  onMouseLeave?: (id: UnpackedHypermediaId) => void
  banner?: boolean
  showSummary?: boolean
  /** Additional document details rendered below the title and summary. */
  details?: ReactNode
  /** Additional details rendered to the left of the card action buttons. */
  actionDetails?: ReactNode
  /** Show the built-in comment action when counts are not supplied by the caller. */
  showCommentAction?: boolean
  /** Hide the inline comments / options-dropdown row. */
  hideInlineActions?: boolean
  relocationOrigin?: DocumentCardActionOrigin
}) {
  const highlighter = useHighlighter()
  const linkProps = useRouteLink(route ?? (docId ? {key: 'document', id: docId} : null))
  const {onClick: routeOnClick, tag: _routeTag, ...linkAttributes} = linkProps
  const navigate = useNavigate()
  const actions = useDocumentActions()
  const draft = actions.getDraft?.(docId)

  const summaryId = useMemo(() => (docId ? hmId(docId.uid, {path: docId.path}) : null), [docId?.uid, docId?.path])
  const interactionSummary = useInteractionSummary(summaryId, {enabled: !interactionSummaryProp})
  const commentCount = interactionSummaryProp?.comments ?? interactionSummary.data?.comments ?? 0

  const baseMetadata = metadata ?? entity?.document?.metadata
  const resolvedMetadata = draft?.metadata ? {...baseMetadata, ...draft.metadata} : baseMetadata
  const textContent = useMemo(() => {
    if (!showSummary) return null
    if (resolvedMetadata?.summary) {
      return resolvedMetadata.summary
    }
    return plainTextOfContent(entity?.document?.content)
  }, [showSummary, resolvedMetadata, entity?.document?.content])

  const explicitCover = resolvedMetadata?.cover
  const explicitIcon = resolvedMetadata?.icon
  // The indexer precomputes the first content image, delivered as a typed
  // sibling of the card's fast metadata (listing callers). Entity-bearing
  // callers already hold the document, so they derive it from content in
  // hand. Either way a card NEVER fetches a document just for a fallback
  // cover — a doc the indexer hasn't derived yet simply renders coverless
  // until the backfill reindex catches up. An icon suppresses the fallback
  // cover entirely, even if a derived value lingers from before the icon
  // was set.
  const indexedFirstImage = explicitIcon ? undefined : firstImageInContent
  const inHandContent = entity?.document?.content
  const firstContentImage = useMemo(() => {
    if (explicitCover || explicitIcon || indexedFirstImage) return undefined
    if (!inHandContent?.length) return undefined
    const block = findFirstBlock<HMBlockImage>(
      inHandContent,
      (b): b is HMBlockImage => b.type === 'Image' && !!(b as any).link,
    )
    return block?.link || undefined
  }, [explicitCover, explicitIcon, indexedFirstImage, inHandContent])
  const coverImage = explicitCover || indexedFirstImage || firstContentImage
  const iconImage = explicitIcon
  const resolvedVisibility = visibility ?? entity?.document?.visibility
  const isPrivate = resolvedVisibility === 'PRIVATE'
  const doc = entity?.document
  const headCount = getVersionHeads(version ?? doc?.version).length

  // Context-driven state for the inline badges.
  const draftId = actions.getDraftId?.(docId) ?? draft?.id

  const menuItems = useDocumentCardMenuItems(docId, doc, relocationOrigin)

  const sharedProps = {
    ...highlighter(docId),
    className: documentCardContainerClassName({banner, hasCover: !!coverImage}),
  }
  const titleClassName = cn(
    'text-foreground font-sans leading-tight! font-bold',
    banner ? 'block text-2xl' : 'line-clamp-2 text-lg',
  )
  const title = resolvedMetadata?.name
  const content = (
    <DocumentCardShell
      interactive={navigateProp}
      hasCover={!!coverImage}
      thumbnail={
        <DocumentCardThumbnail
          coverImage={coverImage}
          iconImage={iconImage}
          kind={getDocumentKind({metadata: resolvedMetadata, isCollection, visibility: resolvedVisibility})}
          banner={banner}
        />
      }
      title={
        titleLinkOnly && linkAttributes.href ? (
          <a
            {...linkAttributes}
            onMouseDown={(e) => {
              e.stopPropagation()
            }}
            onClick={(e) => {
              e.stopPropagation()
              routeOnClick?.(e)
            }}
            className={cn(titleClassName, 'max-w-full cursor-pointer no-underline hover:underline')}
          >
            {title}
          </a>
        ) : (
          <p className={titleClassName}>{title}</p>
        )
      }
      summary={
        textContent ? (
          <p className={cn('text-muted-foreground mt-2 line-clamp-2 font-sans', !banner && 'text-sm')}>{textContent}</p>
        ) : null
      }
      details={details}
      actionDetails={actionDetails}
      badges={
        <>
          {contributorUids && contributorUids.length > 0 && accountsMetadata && (
            <FacePile accounts={contributorUids} accountsMetadata={accountsMetadata} />
          )}
          {!!draftId && <DraftBadge />}
          {!draftId && isPrivate && <PrivateBadge size="sm" />}
          {!draftId && headCount > 1 && <MergedBadge count={headCount} size="sm" />}
        </>
      }
      actions={
        !hideInlineActions ? (
          <div className="flex items-center gap-1">
            {showCommentAction && commentCount > 0 && (
              <Tooltip content="View discussions">
                <Button
                  size="icon"
                  variant="ghost"
                  className="no-window-drag flex items-center gap-1"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    navigate({key: 'comments', id: docId})
                  }}
                >
                  <MessageSquare className="size-3.5" />
                  <SizableText size="xs" className="font-sans">
                    {commentCount}
                  </SizableText>
                </Button>
              </Tooltip>
            )}
            {menuItems.length > 0 && (
              <OptionsDropdown menuItems={menuItems} align="end" side="top" hiddenUntilItemHover />
            )}
          </div>
        ) : null
      }
    />
  )

  if (navigateProp && linkProps) {
    return (
      <a {...sharedProps} {...linkAttributes} onClick={routeOnClick} {...(props as any)}>
        {content}
      </a>
    )
  }

  return (
    <div {...sharedProps} {...props}>
      {content}
    </div>
  )
}
